import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AdminAuthContext = createContext(null);

const ADMIN_TOKEN_KEY = 'ip_admin_token';

// Separate axios instance that injects the admin token
const getAdminHeaders = () => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) { setLoading(false); return; }

    api.get('/admin/auth/me', { headers: getAdminHeaders() })
      .then(({ data }) => setAdmin(data.admin))
      .catch(() => localStorage.removeItem(ADMIN_TOKEN_KEY))
      .finally(() => setLoading(false));
  }, []);

  const adminLogin = useCallback(async (email, password) => {
    const { data } = await api.post('/admin/auth/login', { email, password });
    localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
    setAdmin(data.admin);
    return data;
  }, []);

  const adminLogout = useCallback(async () => {
    try {
      await api.post('/admin/auth/logout', {}, { headers: getAdminHeaders() });
    } catch (_) { /* ignore */ }
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAdmin(null);
  }, []);

  const value = {
    admin,
    loading,
    isAdminAuthenticated: !!admin,
    adminLogin,
    adminLogout,
    getAdminHeaders, // expose so admin API calls can attach the right header
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  return ctx;
}
